import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  Input,
  Label,
  Icon,
} from 'semantic-ui-react';
import {
  startCase,
  includes,
  isNumber,
} from 'lodash';
import numeral from 'numeral';

import { renderCurrencySymbol, renderCurrencyQuantity } from '../../utils/helpers/currencies';

import {
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YoutubeIcon,
  PinterestIcon,
  TwitchIcon,
  BlogIcon,
  TelegramIcon,
  TiktokIcon,
} from '../dyzio-icons';

const defaultNetworks = ['facebook', 'twitter', 'instagram', 'pinterest', 'tiktok',
  'snapchat', 'telegram', 'youtube', 'twitch'];
const hasImages = ['facebook', 'twitter', 'instagram', 'pinterest', 'tiktok', 'snapchat', 'telegram', 'web'];
const hasVideos = ['facebook', 'twitter', 'instagram',
  'youtube', 'twitch', 'tiktok', 'snapchat', 'telegram', 'web'];
const hasStories = ['facebook', 'instagram'];
const NaLabel = (<Label>N/A</Label>);

const UNDEFINED_READONLY_STYLE = {
  color: '#888',
};


const findSocialIcon = network => {
  switch (network) {
  case 'facebook':
    return <FacebookIcon />;
  case 'instagram':
    return <InstagramIcon />;
  case 'twitter':
    return <TwitterIcon />;
  case 'youtube':
    return <YoutubeIcon />;
  case 'pinterest':
    return <PinterestIcon />;
  case 'tiktok':
    return <TiktokIcon />;
  case 'twitch':
    return <TwitchIcon />;
  case 'snapchat':
    return <Icon name="snapchat ghost" color="yellow" />;
  case 'telegram':
    return <TelegramIcon />;
  case 'web':
    return <BlogIcon />;
  default:
    return <BlogIcon />;
  }
};

const SocialRatesFields = ({
  network,
  currency,
  rates,
  readOnly,
  onChange,
  value,
  followers,
}) => {
  const { imageRate, videoRate, storyRate, networkName } = rates || {};

  const isCustom = !includes(defaultNetworks, network);

  const socialIcon = findSocialIcon(network);

  const handleText = value
    ? `${value}`
    : <span style={UNDEFINED_READONLY_STYLE}>{startCase(network)}</span>;

  const followersText = followers
    ? numeral(followers).format('0.0a')
    : null;

  const readOnlyImageRate = imageRate
    ? `${renderCurrencyQuantity({ amount: imageRate, currency })}`
    : <span style={UNDEFINED_READONLY_STYLE}>Image Rate</span>;
  const readOnlyVideoRate = videoRate
    ? `${renderCurrencyQuantity({ amount: videoRate, currency })}`
    : <span style={UNDEFINED_READONLY_STYLE}>Video Rate</span>;
  const readOnlyStoryRate = storyRate
    ? `${renderCurrencyQuantity({ amount: storyRate, currency })}`
    : <span style={UNDEFINED_READONLY_STYLE}>Story Rate</span>;

  const currencyInputProps = {
    label: renderCurrencySymbol(currency),
    onChange,
    fluid: true,
    type: 'number',
    readOnly,
  };

  const nameInputProps = {
    onChange,
    fluid: true,
    readOnly,
  };

  const imageRateDisplay = readOnly
    ? readOnlyImageRate
    : (<Input
      name={`${network}Rates.imageRate`}
      value={imageRate}
      placeholder="Image Rate"
      {...currencyInputProps}
    />);

  const videoRateDisplay = readOnly
    ? readOnlyVideoRate
    : (<Input
      name={`${network}Rates.videoRate`}
      value={videoRate}
      placeholder="Video Rate"
      {...currencyInputProps}
    />);

  const networkNameInput = readOnly
    ? `${networkName} (custom)`
    : (<Input
      name="othersRates.networkName"
      value={networkName}
      placeholder="Network Name"
      {...nameInputProps}
    />);

  const storyRateDisplay = readOnly
    ? readOnlyStoryRate
    : (<Input
      name={`${network}Rates.storyRate`}
      value={storyRate}
      placeholder="Story Rate"
      {...currencyInputProps}
    />);

  return (
    <Table.Row>
      <Table.Cell textAlign="left">
        {socialIcon}
        {isCustom ? networkNameInput : handleText}
      </Table.Cell>

      <Table.Cell collapsing>
        {followersText}
      </Table.Cell>

      <Table.Cell colSpan={1}>
        {
          includes(hasImages, network) || isCustom
            ? imageRateDisplay
            : NaLabel
        }
      </Table.Cell>
      <Table.Cell colSpan={1}>
        {
          includes(hasVideos, network) || isCustom
            ? videoRateDisplay
            : NaLabel
        }
      </Table.Cell>
      <Table.Cell colSpan={1}>
        {
          includes(hasStories, network) || isCustom
            ? storyRateDisplay
            : NaLabel
        }
      </Table.Cell>
    </Table.Row>
  );
};

SocialRatesFields.propTypes = {
  network: PropTypes.string,
  currency: PropTypes.string,
  rates: PropTypes.object,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.string,
  followers: PropTypes.number,
};

export default SocialRatesFields;



/** WEBPACK FOOTER **
 ** ./src/components/influencer-rates-table/social-rates-fields.js
 **/